import React from 'react'
import dayjs from 'dayjs'
import { useFormContext, Controller } from 'react-hook-form'
import { Box, InputLabel } from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'

interface DateTimePickerProps {
  name: string
  label?: string
  fullWidth?: boolean
  required?: boolean
  minDate?: Date
  allowPastDates?: boolean // If true, allow selecting dates before today
  maxDate?: Date
  disabled?: boolean
  placeholder?: string
  /** When true, past dates and times are not selectable (MUI X validation). */
  disablePast?: boolean
  /** When true, shows a clear (X) button so the value can be reset to null. */
  clearable?: boolean
  onChange?: (value: Date | null, keyboardInputValue?: string | undefined) => void
}

const GenericDateTimePicker: React.FC<DateTimePickerProps> = ({
  name,
  label,
  fullWidth = true,
  required,
  minDate,
  allowPastDates = false,
  maxDate,
  disabled,
  placeholder,
  disablePast = false,
  clearable = false,
  onChange = () => { }
}) => {
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const errorMessage = error?.message ?? false
        const { value, onChange: fieldOnChange, onBlur, ref, name: fieldName } = field
        const utc = value ? dayjs.utc(value).tz() : null

        let minDateDayjs: ReturnType<typeof dayjs> | undefined

        if (disablePast) {
          if (minDate != null && minDate instanceof Date && !isNaN(minDate.getTime())) {
            minDateDayjs = dayjs(minDate)
          }
        } else {
          let effectiveMinDate: Date | undefined
          if (allowPastDates) {
            effectiveMinDate = minDate
          } else {
            const currentTimestamp = new Date()
            if (minDate && minDate instanceof Date && !isNaN(minDate.getTime())) {
              effectiveMinDate = minDate.getTime() > currentTimestamp.getTime() ? minDate : currentTimestamp
            } else {
              effectiveMinDate = currentTimestamp
            }
          }

          minDateDayjs =
            effectiveMinDate != null && !isNaN(effectiveMinDate.getTime())
              ? dayjs(effectiveMinDate)
              : undefined
        }

        const maxDateDayjs =
          maxDate != null && !isNaN(maxDate.getTime()) ? dayjs(maxDate) : undefined

        return (
          <Box minHeight={label ? '72px' : undefined}>
            {Boolean(label) && (
              <InputLabel sx={{ marginBottom: 2 }} required={required}>
                {label}
              </InputLabel>
            )}
            <DateTimePicker
              disabled={disabled}
              value={utc}
              minDate={minDateDayjs}
              maxDate={maxDateDayjs}
              disablePast={disablePast}
              onChange={(v) => {
                const toUtc = v ? dayjs.tz(v).utc().toDate() : null
                fieldOnChange(toUtc)
                onChange(toUtc)
              }}
              slotProps={{
                field: clearable ? { clearable: true } : undefined,
                actionBar: clearable ? { actions: ['clear', 'accept'] } : undefined,
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  fullWidth,
                  helperText: errorMessage,
                  error: Boolean(errorMessage),
                  placeholder,
                  onBlur,
                  name: fieldName,
                  inputRef: ref,
                  inputProps: {
                    'aria-required': required ?? false
                  }
                }
              }}
            />
          </Box>
        )
      }}
    />
  )
}

export default GenericDateTimePicker
